import { useState, useEffect, useCallback } from 'react';
import { BookingApi } from 'shared/api';
import {
  ScheduleMode,
  ISchedulePeriod,
  ScheduleFormConfig,
  IWeeklyConfig,
  IDayConfig,
} from '../interfaces/schedule-period.interface';
import {
  convertRulesToPeriods,
  createDefaultWeeklyConfig,
  createDefaultShiftConfig,
  createDefaultDailyConfig,
} from '../lib/convert-rules';
import { IWorkDaysRules } from 'shared/api/booking/interfaces/work-time-rules.interface';
import { IWorkBreaksRules } from 'shared/api/booking/interfaces/work-time-rules.interface';
import { IWorkDayRuleCreateParams } from 'shared/api/booking/interfaces/work-day-rule-create.params.interface';
import { IWorkBreakRuleCreateParams } from 'shared/api/booking/interfaces/work-break-rule-create.params.interface';

const DAY_INDEX: Record<string, number> = {
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
  sunday: 0,
};

function getFirstWeekdayDate(dateStart: string, dayKey: string): string {
  const date = new Date(dateStart);
  let diff = DAY_INDEX[dayKey] - date.getDay();
  if (diff < 0) diff += 7;
  if (diff > 0) date.setDate(date.getDate() + diff);
  return date.toISOString().split('T')[0];
}

interface IUseWorkScheduleProps {
  userId?: string;
  pointId?: string;
  role?: string | null;
}

export function useWorkSchedule({
  userId,
  pointId,
  role,
}: IUseWorkScheduleProps) {
  const [periods, setPeriods] = useState<ISchedulePeriod[]>([]);
  const [rawWorkDays, setRawWorkDays] = useState<IWorkDaysRules[]>([]);
  const [rawWorkBreaks, setRawWorkBreaks] = useState<IWorkBreaksRules[]>([]);
  const [mode, setMode] = useState<ScheduleMode>('weekly');
  const [dateStart, setDateStart] = useState(
    () => new Date().toISOString().split('T')[0]
  );
  const [dateEnd, setDateEnd] = useState('');
  const [formConfig, setFormConfig] = useState<ScheduleFormConfig>(() =>
    createDefaultWeeklyConfig()
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchRules = useCallback(async () => {
    if (!userId || !pointId) return;
    setIsLoading(true);
    setError(null);
    try {
      const rules = await BookingApi.getWorkTimeRules({ userId, pointId });
      const converted = convertRulesToPeriods(rules.workDays, rules.workBreaks);
      setPeriods(converted);
      setRawWorkDays(rules.workDays || []);
      setRawWorkBreaks(rules.workBreaks || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load rules');
    } finally {
      setIsLoading(false);
    }
  }, [userId, pointId]);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const handleModeChange = useCallback((newMode: ScheduleMode) => {
    setMode(newMode);
    switch (newMode) {
      case 'weekly':
        setFormConfig(createDefaultWeeklyConfig());
        break;
      case 'shift':
        setFormConfig(createDefaultShiftConfig());
        break;
      case 'daily':
        setFormConfig(createDefaultDailyConfig());
        break;
    }
  }, []);

  const addPeriod = useCallback(async () => {
    if (!role || !userId || !pointId) {
      setError('Role or userId or pointId is not defined');
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      if (mode === 'daily') {
        const cfg =
          formConfig as import('../interfaces/schedule-period.interface').IDailyConfig;
        const params: IWorkDayRuleCreateParams = {
          daysRule: { countDaysOver: 0, countDaysRunning: 1 },
          dateStart,
          startedAt: cfg.startedAt,
          finishedAt: cfg.finishedAt,
          role,
          userId,
          pointId,
        };
        if (dateEnd) params.dateEnd = dateEnd;
        await BookingApi.workDayRuleCreate(params);
        for (const br of cfg.breaks) {
          const breakParams: IWorkBreakRuleCreateParams = {
            title: br.title,
            timeRule: {
              startedAt: br.start,
              finishedAt: br.end,
              countTimeOver: null,
              countTimeRunning: null,
            },
            daysRule: { countDaysOver: 0, countDaysRunning: 1 },
            dateStart,
            role,
            userId,
            pointId,
          };
          if (dateEnd) breakParams.dateEnd = dateEnd;
          await BookingApi.workBreakRuleCreate(breakParams);
        }
      } else if (mode === 'weekly') {
        const cfg = formConfig as IWeeklyConfig;

        const existingRules = await BookingApi.getWorkTimeRules({
          userId,
          pointId,
        });
        for (const rule of existingRules.workDays || []) {
          if (
            rule.dateStart >= dateStart &&
            (!dateEnd || rule.dateStart <= dateEnd)
          ) {
            await BookingApi.workDayRuleDelete({ workDayId: rule.id });
          }
        }
        for (const rule of existingRules.workBreaks || []) {
          if (
            rule.dateStart >= dateStart &&
            (!dateEnd || rule.dateStart <= dateEnd)
          ) {
            await BookingApi.workBreakRuleDelete({ workBreakId: rule.id });
          }
        }

        const daysToCreate: Array<{
          dayKey: string;
          dayCfg: IDayConfig;
          dayDateStart: string;
        }> = [];
        for (const [dayKey, dayCfg] of Object.entries(cfg)) {
          if (dayCfg.isDayOff || !dayCfg.startedAt || !dayCfg.finishedAt)
            continue;
          daysToCreate.push({
            dayKey,
            dayCfg: {
              isDayOff: dayCfg.isDayOff,
              startedAt: dayCfg.startedAt,
              finishedAt: dayCfg.finishedAt,
              breaks: dayCfg.breaks,
            },
            dayDateStart: getFirstWeekdayDate(dateStart, dayKey),
          });
        }

        daysToCreate.sort((a, b) =>
          a.dayDateStart.localeCompare(b.dayDateStart)
        );

        for (let i = 0; i < daysToCreate.length; i++) {
          const { dayCfg, dayDateStart } = daysToCreate[i];
          const params: IWorkDayRuleCreateParams = {
            daysRule: { countDaysOver: 6, countDaysRunning: 1 },
            dateStart: dayDateStart,
            startedAt: dayCfg.startedAt,
            finishedAt: dayCfg.finishedAt,
            role,
            userId,
            pointId,
          };
          if (dateEnd) params.dateEnd = dateEnd;

          await BookingApi.workDayRuleCreate(params);
          for (const br of dayCfg.breaks) {
            const breakParams: IWorkBreakRuleCreateParams = {
              title: br.title,
              timeRule: {
                startedAt: br.start,
                finishedAt: br.end,
                countTimeOver: null,
                countTimeRunning: null,
              },
              daysRule: { countDaysOver: 6, countDaysRunning: 1 },
              dateStart: dayDateStart,
              role,
              userId,
              pointId,
            };
            if (dateEnd) breakParams.dateEnd = dateEnd;
            await BookingApi.workBreakRuleCreate(breakParams);
          }
        }
      } else if (mode === 'shift') {
        const cfg =
          formConfig as import('../interfaces/schedule-period.interface').IShiftConfig;
        const primaryDay = cfg.workingDays[0];
        const params: IWorkDayRuleCreateParams = {
          daysRule: {
            countDaysOver: cfg.daysOver,
            countDaysRunning: cfg.daysRunning,
          },
          dateStart,
          startedAt: primaryDay?.startedAt || '09:00',
          finishedAt: primaryDay?.finishedAt || '18:00',
          role,
          userId,
          pointId,
        };
        if (dateEnd) params.dateEnd = dateEnd;
        await BookingApi.workDayRuleCreate(params);
      }
      setRefreshKey((k) => k + 1);
      await fetchRules();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save rule');
    } finally {
      setIsSaving(false);
    }
  }, [mode, formConfig, dateStart, dateEnd, role, userId, pointId, fetchRules]);

  const deleteWorkDay = useCallback(
    async (workDayId: string) => {
      setIsSaving(true);
      setError(null);
      try {
        await BookingApi.workDayRuleDelete({ workDayId });
        setRefreshKey((k) => k + 1);
        await fetchRules();
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to delete work day'
        );
      } finally {
        setIsSaving(false);
      }
    },
    [fetchRules]
  );

  const deleteWorkBreak = useCallback(
    async (workBreakId: string) => {
      setIsSaving(true);
      setError(null);
      try {
        await BookingApi.workBreakRuleDelete({ workBreakId });
        setRefreshKey((k) => k + 1);
        await fetchRules();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to delete break');
      } finally {
        setIsSaving(false);
      }
    },
    [fetchRules]
  );

  const deletePeriod = useCallback(
    async (period: ISchedulePeriod) => {
      setIsSaving(true);
      setError(null);
      try {
        for (const ruleId of period.ruleIds) {
          await BookingApi.workDayRuleDelete({ workDayId: ruleId });
        }
        setRefreshKey((k) => k + 1);
        await fetchRules();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to delete rule');
      } finally {
        setIsSaving(false);
      }
    },
    [fetchRules]
  );

  return {
    periods,
    rawWorkDays,
    rawWorkBreaks,
    mode,
    dateStart,
    dateEnd,
    formConfig,
    isLoading,
    isSaving,
    error,
    refreshKey,
    setMode: handleModeChange,
    setDateStart,
    setDateEnd,
    setFormConfig,
    addPeriod,
    deletePeriod,
    deleteWorkDay,
    deleteWorkBreak,
  };
}
